<template>
  <admin-layout :title="$t('edit_x', { x: $t('Sale') })">
    <tec-form-section @submitted="update" :fullWidth="true">
      <template #title>
        <div class="flex items-center">
          <Link class="text-indigo-400 hover:text-indigo-600" :href="route('sales')">{{ $t('Sales') }}</Link>
          <span class="text-indigo-400 font-medium mx-2">/</span>
          {{ $t('Id') }} {{ edit.id }}
        </div>
      </template>

      <template #description>{{ $t('Update the record by modifying the details in the form below') }}</template>

      <template #form>
        <trashed-message v-if="edit.deleted_at" class="mb-6" @restore="restore">
          {{ $t('This record has been deleted.') }}
        </trashed-message>

        <div class="max-w-full col-span-full flex flex-col md:grid sm:grid-cols-2 gap-6">
          <div>
            <text-input
              v-model="form.number"
              :error="$page.props.errors.number ? $page.props.errors.number.replace('{attribute}', 'invoice number') : null"
              :label="$t('Invoice Number')"
            />
          </div>
          <div>
            <text-input
              v-model="form.locator_number"
              :error="$page.props.errors.locator_number ? $page.props.errors.locator_number.replace('{attribute}', 'locator number') : null"
              :label="$t('Locator Number')"
            />
          </div>
          <div>
            <text-input
              type="date"
              v-model="form.date"
              format="yyyy-mm-dd"
              :error="$page.props.errors.date ? $page.props.errors.date.replace('{attribute}', 'date') : null"
              :label="$t('Date')"
            />
          </div>
          <div>
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <v-select-input :label="$t('Customer')">
                <v-select v-model="form.customer_id" :options="customers" :reduce="customer => customer.id" label="name">
                  <template #open-indicator="{ attributes }">
                    <span v-bind="attributes">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </span>
                  </template>
                </v-select>
              </v-select-input>
            </div>
          </div>
          <div class="col-span-full">
            <div class="-mx-4 pb-2 overflow-x-auto min-w-full sm:-mx-6 lg:-mx-8">
              <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div class="overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col" class="py-3.5 pl-4 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-6 w-12">No.</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{{ $t('Description') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Quantity') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Tax') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('HST') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">
                          {{ $t('Agent Commission') }}
                        </th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Commission') }}</th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">
                          {{ $t('HST on Comm.') }}
                        </th>
                        <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-40">{{ $t('Airfare') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) of form.items" :key="'i_' + index" class="odd:bg-white even:bg-gray-50">
                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-center text-sm font-medium text-gray-900 sm:pl-6 w-12">
                          {{ index + 1 }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-2 text-sm min-w-[250px]">
                          <input
                            type="text"
                            v-model="item.description"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.description'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.description']
                                ? $page.props.errors['items.' + index + '.description'].replace('{attribute}', 'description')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.quantity"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.quantity'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.quantity']
                                ? $page.props.errors['items.' + index + '.quantity'].replace('{attribute}', 'quantity')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.tax"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.tax'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.tax']
                                ? $page.props.errors['items.' + index + '.tax'].replace('{attribute}', 'tax')
                                : null
                            }}
                          </div>
                        </td>
                        <!-- <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <select
                            v-model="item.sale_id"
                            class="w-full focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.sale_id'] ? 'border-red-500' : 'border-gray-200'"
                          >
                            <option value=""></option>
                            <option v-for="sale of sales" :value="sale.id" :key="'t_' + sale.id">
                              {{ sale.name }}
                            </option>
                          </select>
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.sale_id']
                                ? $page.props.errors['items.' + index + '.sale_id'].replace(
                                    '{attribute}',
                                    'sale_id'
                                  )
                                : null
                            }}
                          </div>
                        </td> -->
                        <td class="whitespace-nowrap px-3 py-2 text-sm w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.hst"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.hst'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.hst']
                                ? $page.props.errors['items.' + index + '.hst'].replace('{attribute}', 'hst')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap px-3 py-2 text-right text-sm font-medium w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.agent_commission"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.agent_commission'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.agent_commission']
                                ? $page.props.errors['items.' + index + '.agent_commission'].replace('{attribute}', 'agent commission')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap px-3 py-2 text-right text-sm font-medium w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.commission"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.commission'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.commission']
                                ? $page.props.errors['items.' + index + '.commission'].replace('{attribute}', 'commission')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap px-3 py-2 text-right text-sm font-medium w-40 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.hst_on_commission"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.hst_on_commission'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.hst_on_commission']
                                ? $page.props.errors['items.' + index + '.hst_on_commission'].replace('{attribute}', 'hst_on_commission')
                                : null
                            }}
                          </div>
                        </td>
                        <td class="relative whitespace-nowrap px-3 py-2 text-right text-sm font-medium sm:pr-6 w-48 min-w-[150px]">
                          <input
                            type="number"
                            step="0.01"
                            v-model="item.airfare"
                            class="border w-full py-2 px-4 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md"
                            :class="$page.props.errors['items.' + index + '.airfare'] ? 'border-red-500' : 'border-gray-200'"
                          />
                          <div class="text-sm text-red-600 mt-1">
                            {{
                              $page.props.errors['items.' + index + '.airfare']
                                ? $page.props.errors['items.' + index + '.airfare'].replace('{attribute}', 'airfare')
                                : null
                            }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot class="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          class="pb-1 pt-3.5 border-b pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                          colspan="2"
                        >
                          <!-- {{ $t('Total') }} -->
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          {{ $number(form.items.reduce((a, i) => (i.quantity ? a + Number(i.quantity) : a), 0)) }}
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{ $number(form.items.reduce((a, i) => (i.tax ? a + Number(i.quantity) * Number(i.tax) : a), 0)) }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{ $number(form.items.reduce((a, i) => (i.hst ? a + Number(i.quantity) * Number(i.hst) : a), 0)) }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce(
                                  (a, i) => (i.agent_commission ? a + Number(i.quantity) * Number(i.agent_commission) : a),
                                  0
                                )
                              )
                            }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(form.items.reduce((a, i) => (i.commission ? a + Number(i.quantity) * Number(i.commission) : a), 0))
                            }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce(
                                  (a, i) => (i.hst_on_commission ? a + Number(i.quantity) * Number(i.hst_on_commission) : a),
                                  0
                                )
                              )
                            }}
                          </div>
                        </th>
                        <th scope="col" class="px-3 pb-1 pt-3.5 border-b text-right text-sm font-bold sm:pr-6 text-gray-900 w-48">
                          <div class="px-4">
                            {{ $number(form.items.reduce((a, i) => (i.airfare ? a + Number(i.quantity) * Number(i.airfare) : a), 0)) }}
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th scope="col" class="py-1 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" colspan="2">
                          {{ $t('Total Airfare') }}
                        </th>
                        <th scope="col" class="px-3 py-1 text-left text-sm font-semibold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-48"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold sm:pr-6 text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce((a, i) => (i.airfare ? a + Number(i.quantity) * Number(i.airfare) : a), 0) +
                                  form.items.reduce((a, i) => (i.commission ? a + Number(i.quantity) * Number(i.commission) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.agent_commission ? a + Number(i.quantity) * Number(i.agent_commission) : a),
                                    0
                                  )
                              )
                            }}
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th scope="col" class="py-1 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" colspan="2">
                          {{ $t('Total Tax') }}
                        </th>
                        <th scope="col" class="px-3 py-1 text-left text-sm font-semibold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold text-gray-900 w-48"></th>
                        <th scope="col" class="px-3 py-1 text-right text-sm font-bold sm:pr-6 text-gray-900 w-40">
                          <div class="px-4">
                            {{
                              $number(
                                form.items.reduce((a, i) => (i.tax ? a + Number(i.quantity) * Number(i.tax) : a), 0) +
                                  form.items.reduce((a, i) => (i.hst ? a + Number(i.quantity) * Number(i.hst) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.hst_on_commission ? a + Number(i.quantity) * Number(i.hst_on_commission) : a),
                                    0
                                  )
                              )
                            }}
                          </div>
                        </th>
                      </tr>
                      <tr>
                        <th scope="col" class="border-t text-lg py-3.5 pl-4 pr-3 text-left font-semibold text-gray-900 sm:pl-6" colspan="2">
                          {{ $t('Grand Total') }}
                        </th>
                        <th scope="col" class="px-3 border-t py-3.5 text-left text-sm font-semibold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-40"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold text-gray-900 w-48"></th>
                        <th scope="col" class="px-3 border-t py-3.5 text-right text-sm font-bold sm:pr-6 text-gray-900 w-40">
                          <div class="px-4 text-lg">
                            {{
                              $number(
                                form.items.reduce((a, i) => (i.tax ? a + Number(i.quantity) * Number(i.tax) : a), 0) +
                                  form.items.reduce((a, i) => (i.hst ? a + Number(i.quantity) * Number(i.hst) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.hst_on_commission ? a + Number(i.quantity) * Number(i.hst_on_commission) : a),
                                    0
                                  ) +
                                  form.items.reduce((a, i) => (i.airfare ? a + Number(i.quantity) * Number(i.airfare) : a), 0) +
                                  form.items.reduce((a, i) => (i.commission ? a + Number(i.quantity) * Number(i.commission) : a), 0) +
                                  form.items.reduce(
                                    (a, i) => (i.agent_commission ? a + Number(i.quantity) * Number(i.agent_commission) : a),
                                    0
                                  )
                              )
                            }}
                          </div>
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            <span class="relative z-0 inline-flex rounded-md">
              <button
                type="button"
                @click="addRow()"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <span class="sr-only">Add new row</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
              <button
                type="button"
                @click="removeRow()"
                class="-ml-px relative inline-flex items-center px-2 py-1 rounded-r-md border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <span class="sr-only">Remove last row</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 12H6" />
                </svg>
              </button>
            </span>
          </div>
          <div>
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <select-input
                v-model="form.status"
                :error="$page.props.errors.status ? $page.props.errors.status.replace('{attribute}', $t('status')) : null"
                :label="$t('Status')"
              >
                <option value="Pending">{{ $t('Pending') }}</option>
                <option value="Paid">{{ $t('Paid') }}</option>
                <option value="Overdue">{{ $t('Overdue') }}</option>
                <option value="Canceled">{{ $t('Canceled') }}</option>
              </select-input>
            </div>
          </div>
          <div>
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <select-input
                v-model="form.payment_method"
                :error="
                  $page.props.errors.payment_method ? $page.props.errors.payment_method.replace('{attribute}', $t('payment method')) : null
                "
                :label="$t('Payment Method')"
              >
                <option value=""></option>
                <option value="Credit Card">{{ $t('Credit Card') }}</option>
                <option value="Cash">{{ $t('Cash') }}</option>
                <option value="Debit Card">{{ $t('Debit Card') }}</option>
                <option value="Transfer">{{ $t('Transfer') }}</option>
              </select-input>
            </div>
          </div>
          <div v-if="form.payment_method == 'Credit Card'">
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <text-input
                v-model="form.cc_fees"
                :error="$page.props.errors.cc_fees ? $page.props.errors.cc_fees.replace('{attribute}', 'credit card fee') : null"
                :label="$t('Credit Card Fee')"
              />
            </div>
          </div>
          <div v-if="$super">
            <div class="col-span-6 sm:col-span-4 lg:col-span-4">
              <select-input
                :label="$t('Agent')"
                v-model="form.agent_id"
                :error="$page.props.errors.agent_id ? $page.props.errors.agent_id.replace('{attribute}', $t('agent')) : null"
              >
                <option value=""></option>
                <option v-for="agent in users" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
              </select-input>
            </div>
          </div>

          <div class="col-span-full">
            <label for="comment" class="block text-sm font-medium text-gray-700">{{ $t('Comment') }}</label>
            <div class="mt-1">
              <textarea
                rows="4"
                id="comment"
                v-model="form.comment"
                class="shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 block w-full sm:text-sm border-gray-300 rounded-md"
              ></textarea>
            </div>
          </div>
          <div class="col-span-full">
            <div class="flex items-center">
              <button
                type="button"
                @click="form.travel_insurance = !form.travel_insurance"
                :class="form.travel_insurance ? 'bg-indigo-600' : 'bg-gray-200'"
                class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                role="switch"
                aria-checked="false"
                aria-labelledby="annual-billing-label"
              >
                <span
                  aria-hidden="true"
                  :class="form.travel_insurance ? 'translate-x-5' : 'translate-x-0'"
                  class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                ></span>
              </button>
              <span class="ml-3 cursor-pointer" id="annual-billing-label" @click="form.travel_insurance = !form.travel_insurance">
                <span class="text-sm font-medium text-gray-900">Travel Insurance</span>
              </span>
            </div>
          </div>
          <div class="col-span-full">
            <div class="flex items-center">
              <button
                type="button"
                @click="form.medical_insurance = !form.medical_insurance"
                :class="form.medical_insurance ? 'bg-indigo-600' : 'bg-gray-200'"
                class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                role="switch"
                aria-checked="false"
                aria-labelledby="annual-billing-label"
              >
                <span
                  aria-hidden="true"
                  :class="form.medical_insurance ? 'translate-x-5' : 'translate-x-0'"
                  class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                ></span>
              </button>
              <span class="ml-3 cursor-pointer" id="annual-billing-label" @click="form.medical_insurance = !form.medical_insurance">
                <span class="text-sm font-medium text-gray-900">Medical Insurance</span>
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #actions>
        <div class="w-full flex items-center justify-between">
          <button
            type="button"
            @click="destroy"
            v-if="!edit.deleted_at"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('Sale') }) }}
          </button>
          <button
            v-else
            type="button"
            @click="destroyPermanently"
            class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
          >
            {{ $t('delete_x', { x: $t('Permanently') }) }}
          </button>
          <div class="flex items-center">
            <tec-action-message :on="form.recentlySuccessful" class="mr-3">{{ $t('Saved.') }}</tec-action-message>
            <loading-button type="submit" :loading="form.processing" :disabled="form.processing">{{ $t('Save') }}</loading-button>
          </div>
        </div>
      </template>
    </tec-form-section>

    <!-- Delete User Confirmation Modal -->
    <Dialog
      max-width="md"
      :show="permanent"
      action-type="delete"
      title="Delete Sale?"
      :close="closePermanentModal"
      :action="deleteSalePermanently"
      action-text="Delete Permanently"
      :content="`<p class='mb-2'>${$t('Are you sure you want to delete the record permanently?')}</p>
        <p>${$t('Once deleted, all of its resources and data will be permanently deleted.')}</p>`"
    />

    <!-- Delete Account Confirmation Modal -->
    <Dialog
      :show="confirm"
      :close="closeModal"
      :action="deleteTaxRate"
      action-type="delete"
      title="Delete Sale?"
      action-text="Delete Sale"
      :content="$t('Are you sure you want to delete the record?')"
    />

    <!-- Restore Account Confirmation Modal -->
    <Dialog
      :show="restoreConf"
      :action="restoreTaxRate"
      title="Restore Sale!"
      :close="closeRestoreModal"
      action-text="Restore Sale"
      :content="$t('Are you sure you want to restore the record?')"
    />
  </admin-layout>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';

import Dialog from '@/Shared/Dialog';
import TextInput from '@/Shared/TextInput';
import FileInput from '@/Shared/FileInput';
import SelectInput from '@/Shared/SelectInput';
import AdminLayout from '@/Layouts/AdminLayout';
import VSelectInput from '@/Shared/VSelectInput';
import LoadingButton from '@/Shared/LoadingButton';
import TecDialogModal from '@/Jetstream/DialogModal';
import TrashedMessage from '@/Shared/TrashedMessage';
import TecFormSection from '@/Jetstream/FormSection';
import TecDangerButton from '@/Jetstream/DangerButton';
import TecSectionTitle from '@/Jetstream/SectionTitle';
import TecActionMessage from '@/Jetstream/ActionMessage';
import TecSecondaryButton from '@/Jetstream/SecondaryButton';

export default {
  props: ['edit', 'customers', 'sales', 'users'],

  components: {
    Link,
    Dialog,
    FileInput,
    TextInput,
    AdminLayout,
    SelectInput,
    VSelectInput,
    LoadingButton,
    TecDialogModal,
    TecFormSection,
    TecDangerButton,
    TrashedMessage,
    TecSectionTitle,
    TecActionMessage,
    TecSecondaryButton,
  },

  data() {
    return {
      confirm: false,
      permanent: false,
      restoreConf: false,
      form: this.$inertia.form({
        _method: 'put',
        number: this.edit.number,
        locator_number: this.edit.locator_number,
        date: this.edit.date.split('T').shift(),
        customer_id: this.edit.customer_id,
        agent_id: this.edit.agent_id,
        payment_method: this.edit.payment_method,
        cc_fees: this.edit.cc_fees,
        comment: this.edit.comment,
        status: this.edit.status,
        travel_insurance: this.edit.travel_insurance == 1,
        medical_insurance: this.edit.medical_insurance == 1,
        items: this.edit.items.map(i => {
          return {
            description: i.description,
            quantity: Number(i.quantity),
            hst: Number(i.hst),
            sale_id: Number(i.sale_id),
            tax: Number(i.tax),
            agent_commission: Number(i.agent_commission),
            commission: Number(i.commission),
            hst_on_commission: Number(i.hst_on_commission),
            airfare: Number(i.airfare),
          };
        }),
      }),
    };
  },

  mounted() {
    console.log(this.form.items.length);
    if (this.form.items.length < 5) {
      for (let i = this.form.items.length; i < 5; i++) {
        this.form.items.push({
          quantity: null,
          hst: null,
          description: null,
          sale_id: null,
          tax: null,
          agent_commission: null,
          commission: null,
          hst_on_commission: null,
          airfare: null,
        });
      }
    }
  },

  methods: {
    update() {
      this.form.post(this.route('sales.update', this.edit.id));
    },
    destroy() {
      this.confirm = true;
    },
    deleteTaxRate() {
      this.form.delete(route('sales.destroy', this.edit.id), {
        onSuccess: () => this.closeModal(),
      });
    },
    closeModal() {
      this.confirm = false;
    },
    restore() {
      this.restoreConf = true;
    },
    restoreTaxRate() {
      this.$inertia.put(this.route('sales.restore', this.edit.id), {
        onSuccess: () => (this.restoreConf = false),
      });
    },
    closeRestoreModal() {
      this.restoreConf = false;
    },
    deletePermanently() {
      this.permanent = true;
    },
    deleteSalePermanently() {
      this.form.delete(route('roles.destroy.permanently', this.edit.id), {
        onSuccess: () => this.closeModal(),
      });
    },
    closePermanentModal() {
      this.permanent = false;
    },
  },
};
</script>
